/**
 * Filter controls for the ViewAllLinksModal.
 *
 * Extracted from ViewAllLinksModal.tsx (Sub-PR 6.4).
 */

import { useTranslation } from 'react-i18next'
import { Filter, Search } from 'lucide-react'
import type { DirectionFilter } from './types'
import { formatType } from './helpers'

interface FilterBarProps {
  searchTerm: string
  setSearchTerm: (s: string) => void
  directionFilter: DirectionFilter
  setDirectionFilter: (d: DirectionFilter) => void
  statusFilter: string
  setStatusFilter: (s: string) => void
  typeFilter: string
  setTypeFilter: (s: string) => void
  uniqueStatuses: string[]
  uniqueTypes: string[]
}

export function FilterBar({
  searchTerm,
  setSearchTerm,
  directionFilter,
  setDirectionFilter,
  statusFilter,
  setStatusFilter,
  typeFilter,
  setTypeFilter,
  uniqueStatuses,
  uniqueTypes,
}: FilterBarProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="border-b border-slate-200 bg-slate-50 px-6 py-3 dark:border-slate-700 dark:bg-slate-800/50">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
            }}
            placeholder={t('infrastructure.viewAllLinks.searchPlaceholder')}
            className="w-full rounded-md border border-slate-300 bg-white py-1.5 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <select
            value={directionFilter}
            onChange={(e) => {
              setDirectionFilter(e.target.value as DirectionFilter)
            }}
            className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          >
            <option value="all">{t('infrastructure.viewAllLinks.allDirections')}</option>
            <option value="outgoing">{t('infrastructure.viewAllLinks.outgoingOnly')}</option>
            <option value="incoming">{t('infrastructure.viewAllLinks.incomingOnly')}</option>
          </select>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
          }}
          className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        >
          <option value="all">{t('infrastructure.viewAllLinks.allStatuses')}</option>
          {uniqueStatuses.map((status) => (
            <option key={status} value={status}>
              {status.replaceAll('-', ' ')}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value)
          }}
          className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        >
          <option value="all">{t('infrastructure.viewAllLinks.allTypes')}</option>
          {uniqueTypes.map((type) => (
            <option key={type} value={type}>
              {formatType(type)}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
